import React, { useState, useEffect } from "react";
import { Button, Form, Icon } from "tabler-react";
import { Crud_Catalogos } from "../../herramientas/Crud_Catalogos";
import { NotificationManager } from 'react-notifications';
import "./OperadoresForm.css";

const OperadoresForm = ({
  accion,
  elementos,
  cerrarModal,
  idTransportista: idTransportistaProp,
  onOperadorGuardado,
  // Cuando es true (solo catálogo normal de operadores), se crea un registro por
  // cada plataforma del usuario (transportista de menor id por plataforma).
  // En cualquier otro flujo (naviero, etc.) se liga solo a idTransportista.
  multiPlataforma = false
}) => {
  const [formulario, setFormulario] = useState({
    nombre: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    contacto: "",
    vigenciaLicencia: "",
    tipoLicencia: ""
  });

  const [crudbutonEnable, setCrudbutonEnable] = useState(true);
  const [idTransportista, setIdTransportista] = useState(null);
  // Un transportista representativo (menor id) por cada plataforma del usuario.
  const [transportistasPorPlataforma, setTransportistasPorPlataforma] = useState([]);

  useEffect(() => {
    // Obtener id_transportista de la prop (flujo edición / naviero).
    if (idTransportistaProp) {
      setIdTransportista(idTransportistaProp);
    }

    // Calcular SIEMPRE un transportista por plataforma (menor id) para poder
    // crear un registro por cada plataforma del usuario en la creación de
    // catálogo. Debe ejecutarse aunque llegue idTransportistaProp.
    {
      const usuario = JSON.parse(sessionStorage.getItem("usuario"));
      const currentUser = JSON.parse(sessionStorage.getItem("currentUser"));
      if (usuario && currentUser) {
        const plataformasIds = [...new Set(currentUser.map(c => c.plataforma?.id).filter(Boolean))];
        Promise.all(plataformasIds.map(idPlat =>
          Crud_Catalogos("usuarios_transportistas/usuario/" + usuario.id + "/plataforma/" + idPlat, "", "get", "", [], "", "", "", [])
            .then(res => ({ idPlataforma: idPlat, transportistas: res || [] }))
            .catch(() => ({ idPlataforma: idPlat, transportistas: [] }))
        )).then((resultados) => {
          // Por cada plataforma, elegir UN transportista (el de menor id -> determinista)
          const seleccion = resultados
            .map(r => {
              const ids = [...new Set(
                (r.transportistas || [])
                  .filter(t => t.idTransportista !== null)
                  .map(t => t.idTransportista)
              )].sort((a, b) => a - b);
              return ids.length > 0 ? { idPlataforma: r.idPlataforma, idTransportista: ids[0] } : null;
            })
            .filter(Boolean);

          setTransportistasPorPlataforma(seleccion);
          if (!idTransportistaProp && seleccion.length > 0) {
            setIdTransportista(seleccion[0].idTransportista);
          }
        }).catch(err => console.log("Error al cargar transportista:", err));
      }
    }

    // Si es edición, cargar datos del operador
    if (accion === "editar" && elementos) {
      // El elementos ahora es directamente el objeto operador (id, nombre, paterno, materno, etc.)
      let fechaFormateada = "";
      if (elementos.licenciaVigencia) {
        const fecha = elementos.licenciaVigencia;
        
        // Si es un número (timestamp en milisegundos)
        if (typeof fecha === 'number') {
          const d = new Date(fecha);
          fechaFormateada = d.toISOString().split('T')[0];
        } else {
          const fechaStr = String(fecha);
          const meses = {
            'enero': '01', 'febrero': '02', 'marzo': '03', 'abril': '04',
            'mayo': '05', 'junio': '06', 'julio': '07', 'agosto': '08',
            'septiembre': '09', 'octubre': '10', 'noviembre': '11', 'diciembre': '12'
          };
          const partes = fechaStr.split(' ');
          const mesTexto = partes[1]?.toLowerCase();
          if (partes.length === 3 && meses[mesTexto]) {
            const dia = partes[0].padStart(2, '0');
            const mes = meses[mesTexto];
            const anio = partes[2];
            fechaFormateada = `${anio}-${mes}-${dia}`;
          } else if (fechaStr.includes('T')) {
            fechaFormateada = fechaStr.split('T')[0];
          } else if (fechaStr.match(/^\d{4}-\d{2}-\d{2}/)) {
            fechaFormateada = fechaStr.substring(0, 10);
          } else {
            // Intentar parsear como Date genérico
            const d = new Date(fechaStr);
            if (!isNaN(d.getTime())) {
              fechaFormateada = d.toISOString().split('T')[0];
            }
          }
        }
      }

      // Si paterno y materno están vacíos pero el nombre contiene espacios,
      // es probable que el nombre completo esté todo en el campo nombre → dividirlo
      let nombreFinal = elementos.nombre || "";
      let paternoFinal = elementos.paterno || "";
      let maternoFinal = elementos.materno || "";

      if (!paternoFinal && !maternoFinal && nombreFinal.trim().includes(" ")) {
        const partes = nombreFinal.trim().split(/\s+/);
        nombreFinal   = partes[0] || "";
        paternoFinal  = partes[1] || "";
        maternoFinal  = partes.slice(2).join(" ") || "";
      }

      setFormulario({
        nombre: nombreFinal,
        apellidoPaterno: paternoFinal,
        apellidoMaterno: maternoFinal,
        contacto: elementos.medioComunicacion || "",
        vigenciaLicencia: fechaFormateada,
        tipoLicencia: elementos.licenciaTipo || ""
      });
    }
  }, [accion, elementos]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    setCrudbutonEnable(false);

    if (!idTransportista) {
      NotificationManager.error(
        "No se pudo obtener el transportista. Recarga la página e intenta nuevamente.",
        "Error de configuración"
      );
      setCrudbutonEnable(true);
      return;
    }

    // DTO para la entidad operadores
    const operadorDTO = {
      nombre: formulario.nombre.trim(),
      paterno: formulario.apellidoPaterno.trim(),
      materno: formulario.apellidoMaterno.trim(),
      medioComunicacion: formulario.contacto.trim(),
      licenciaTipo: formulario.tipoLicencia.trim(),
      licenciaVigencia: formulario.vigenciaLicencia 
        ? new Date(formulario.vigenciaLicencia + "T12:00:00").getTime() 
        : null,
      idTransportista: idTransportista
    };

    // Si es edición, incluir el id
    if (accion === "editar" && elementos?.id) {
      operadorDTO.id = elementos.id;
    }

    if (accion === "editar") {
      // Edición: se mantiene el transportista existente del operador (un solo registro)
      Crud_Catalogos("operadores", "", "put", "", operadorDTO, "", "", "", [])
        .then((response) => {
          NotificationManager.success("Operador actualizado exitosamente", "Éxito");
          if (onOperadorGuardado) {
            onOperadorGuardado(response);
          }
          cerrarModal();
        })
        .catch(err => manejarError(err));
      return;
    }

    // Creación:
    // - Catálogo normal (multiPlataforma=true): un operador por cada plataforma
    //   (transportista de menor id por plataforma).
    // - Cualquier otro flujo (naviero, etc.): se liga solo a idTransportista.
    const objetivos = (multiPlataforma && transportistasPorPlataforma.length > 0)
      ? transportistasPorPlataforma.map(sel => sel.idTransportista)
      : [idTransportistaProp || idTransportista];
    // Deduplicar por si dos plataformas comparten el mismo transportista
    const idsTransportistaUnicos = [...new Set(objetivos.filter(Boolean))];

    Promise.all(idsTransportistaUnicos.map(idT =>
      Crud_Catalogos("operadores", "", "post", "", { ...operadorDTO, idTransportista: idT }, "", "", "", [])
    ))
      .then((responses) => {
        NotificationManager.success(
          `Operador creado exitosamente (${responses.length} registro${responses.length === 1 ? "" : "s"})`,
          "Éxito"
        );
        if (onOperadorGuardado) {
          onOperadorGuardado(responses);
        }
        cerrarModal();
      })
      .catch(err => manejarError(err));
  };

  const manejarError = (err) => {
    console.error("Error al guardar operador:", err);
    const mensaje = err.response?.data?.message || err.response?.data || "Error al guardar el operador";

    if (typeof mensaje === 'string' && (mensaje.toLowerCase().includes("duplicado") || mensaje.toLowerCase().includes("existe") || mensaje.toLowerCase().includes("unique"))) {
      NotificationManager.warning(
        `Ya existe un operador con ese nombre para este transportista`,
        "Operador Duplicado"
      );
    } else {
      NotificationManager.error(String(mensaje), "Error");
    }
    setCrudbutonEnable(true);
  };

  return (
    <div className="modern-operator-container">
      <div className="modern-header">
        <div className="header-content-modern">
          <div className="header-left-section">
            <div className="animated-icon">
              <Icon name="user" className="main-icon" />
            </div>
            <div className="header-text-section">
              <h1 className="modern-title">
                <span className="title-word">{accion === "nuevo" ? "NUEVO" : "EDITAR"}</span>
                <span className="title-word">OPERADOR</span>
              </h1>
            </div>
          </div>
        </div>
      </div>

      <Form onSubmit={handleSubmit} className="modern-form">
        <div className="section-card">
          <div className="section-header">
            <Icon name="user" className="section-icon" />
            <h4 className="section-title">INFORMACIÓN DEL OPERADOR</h4>
          </div>

          <div className="form-grid">
            <div className="form-field">
              <label className="field-label">
                <Icon name="user" className="field-icon" />
                Nombre
              </label>
              <div className="field-content">
                <Form.Input 
                  className="modern-input" 
                  name="nombre" 
                  required 
                  value={formulario.nombre}
                  onChange={(e) => setFormulario({...formulario, nombre: e.target.value})}
                  placeholder="Nombre(s)"
                />
              </div>
            </div>

            <div className="form-field">
              <label className="field-label">
                <Icon name="user" className="field-icon" />
                Apellido Paterno
              </label>
              <div className="field-content">
                <Form.Input 
                  className="modern-input" 
                  name="apellidoPaterno" 
                  value={formulario.apellidoPaterno}
                  onChange={(e) => setFormulario({...formulario, apellidoPaterno: e.target.value})}
                  placeholder="Apellido paterno"
                />
              </div>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-field">
              <label className="field-label">
                <Icon name="user" className="field-icon" />
                Apellido Materno
              </label>
              <div className="field-content">
                <Form.Input 
                  className="modern-input" 
                  name="apellidoMaterno" 
                  value={formulario.apellidoMaterno}
                  onChange={(e) => setFormulario({...formulario, apellidoMaterno: e.target.value})}
                  placeholder="Apellido materno"
                />
              </div>
            </div>

            <div className="form-field">
              <label className="field-label">
                <Icon name="phone" className="field-icon" />
                Contacto
              </label>
              <div className="field-content">
                <Form.Input 
                  className="modern-input" 
                  name="contacto" 
                  required 
                  value={formulario.contacto}
                  onChange={(e) => setFormulario({...formulario, contacto: e.target.value})}
                  placeholder="Teléfono o email"
                />
              </div>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-field">
              <label className="field-label">
                <Icon name="credit-card" className="field-icon" />
                Tipo de Licencia
              </label>
              <div className="field-content">
                <Form.Input 
                  className="modern-input" 
                  name="tipoLicencia" 
                  required 
                  value={formulario.tipoLicencia}
                  onChange={(e) => setFormulario({...formulario, tipoLicencia: e.target.value})}
                  placeholder="Ej: Federal"
                />
              </div>
            </div>

            <div className="form-field">
              <label className="field-label">
                <Icon name="calendar" className="field-icon" />
                Vigencia de Licencia
              </label>
              <div className="field-content">
                <Form.Input 
                  type="date" 
                  className="modern-input" 
                  name="vigenciaLicencia" 
                  required 
                  value={formulario.vigenciaLicencia}
                  onChange={(e) => setFormulario({...formulario, vigenciaLicencia: e.target.value})}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn modern-register-btn" disabled={!crudbutonEnable}>
            <Icon name="save" className="btn-icon" />
            {accion === "nuevo" ? "Registrar" : "Actualizar"}
          </button>
        </div>
      </Form>
    </div>
  );
};

export default OperadoresForm;
